import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

const CartContext = createContext();
const STORAGE_KEY = 'dfwfitmeals_cart';

function loadCart() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch { return []; }
}

export function CartProvider({ children }) {
  const [items, setItems] = useState(loadCart);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  const addItem = useCallback((product, qty = 1) => {
    setItems(prev => {
      const idx = prev.findIndex(i => i.slug === product.slug);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], qty: next[idx].qty + qty };
        return next;
      }
      return [...prev, {
        slug: product.slug,
        name: product.name,
        price: product.price || 0,
        imageUrl: product.imageUrl,
        category: product.category,
        calories: product.calories,
        protein: product.protein,
        pradoProductId: product.pradoProductId,
        pradoVariantId: product.pradoVariantId,
        sourceUrl: product.sourceUrl,
        qty,
      }];
    });

    // GA4 add_to_cart event
    if (window.gtag) {
      window.gtag('event', 'add_to_cart', {
        currency: 'USD',
        value: product.price || 0,
        items: [{ item_id: product.slug, item_name: product.name, price: product.price, quantity: qty }],
      });
    }
  }, []);

  const removeItem = useCallback((slug) => {
    setItems(prev => prev.filter(i => i.slug !== slug));
  }, []);

  const updateQty = useCallback((slug, qty) => {
    if (qty < 1) return removeItem(slug);
    setItems(prev => prev.map(i => i.slug === slug ? { ...i, qty } : i));
  }, [removeItem]);

  const clearCart = useCallback(() => setItems([]), []);

  const itemCount = items.reduce((s, i) => s + i.qty, 0);
  const subtotal = items.reduce((s, i) => s + i.price * i.qty, 0);
  const totalCalories = items.reduce((s, i) => s + (i.calories || 0) * i.qty, 0);
  const totalProtein = items.reduce((s, i) => s + (i.protein || 0) * i.qty, 0);

  return (
    <CartContext.Provider value={{
      items, addItem, removeItem, updateQty, clearCart,
      itemCount, subtotal, totalCalories, totalProtein,
    }}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be inside CartProvider');
  return ctx;
}
